/**
 * The public site and docs pages, in one list.
 *
 * These are the pages served under every locale prefix (/ja/docs/..., ...),
 * prerendered at build time and listed in the sitemap. App.tsx maps each
 * entry to its component through a Record typed on these unions, so a page
 * cannot be routed without being listed here, or listed without a component.
 */

/** Pages under the landing layout, relative to "/" ('' is the home page). */
export const PUBLIC_PAGES = ['', 'scenarios', 'providers', 'local-ai', 'privacy', 'download'] as const;

/** Pages under the docs layout, relative to "/docs" ('' is the docs home). */
export const DOCS_PAGES = [
  '',
  'install/windows',
  'install/linux',
  'install/macos',
  'supported-sites',
  'ai-providers',
  'features/participant-audio',
  'features/subtitle-mode',
  'features/caption-sharing',
  'features/translation-modes',
  'features/virtual-microphone',
  'features/text-only',
  'features/display',
  'features/pronunciation-readings',
  'features/export',
  'features/replay',
  'features/noise-suppression',
  'features/passthrough',
  'features/switch-devices',
  'features/diagnostic-logs',
  'features/interface-language',
  'privacy',
  'delete-account',
  'terms',
  'commerce',
  'tutorials/zoom',
  'tutorials/google-meet',
  'tutorials/microsoft-teams',
  'tutorials/discord',
  'tutorials/slack',
  'tutorials/jitsi',
  'tutorials/whereby',
  'tutorials/gather',
  'tutorials/openai-setup',
  'tutorials/gemini-setup',
  'tutorials/palabraai-setup',
  'tutorials/volcengine-ast2-setup',
  'tutorials/soniox-setup',
  'tutorials/qwen-setup',
  'kizuna-ai/quick-start',
  'kizuna-ai/understand-others',
  'kizuna-ai/be-understood',
  'kizuna-ai/subtitle-my-speech',
  'kizuna-ai/two-way-meeting',
  'kizuna-ai/two-way-subtitles',
  'kizuna-ai/face-to-face',
  'kizuna-ai/voices',
  'kizuna-ai/vocabulary',
  'kizuna-ai/region',
  'kizuna-ai/latency',
  'kizuna-ai/billing',
  'tutorials/local-inference-setup',
  'local-models',
  'tutorials/realtime-api-tester',
] as const;

export type PublicPage = (typeof PUBLIC_PAGES)[number];
export type DocsPage = (typeof DOCS_PAGES)[number];

/**
 * Pages that read the same in every locale and so have one URL only. The
 * commerce disclosure is required by Japanese law to be shown in Japanese.
 */
export const SINGLE_LOCALE_PAGES: ReadonlySet<string> = new Set(['/docs/commerce']);

const join = (base: string, page: string) => (page ? `${base}/${page}` : base || '/');

/** Every page as an unprefixed absolute path: '/', '/scenarios', '/docs', '/docs/install/windows', ... */
export function sitePaths(): string[] {
  return [...PUBLIC_PAGES.map((p) => join('', p)), ...DOCS_PAGES.map((p) => join('/docs', p))];
}
